import { MouseEventHandler, ReactNode, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { ReactComponent as CheckboxBorder } from "../../assets/checkbox-border.svg";
import { ReactComponent as Check } from "../../assets/check.svg";
import { ReactComponent as Remove } from "../../assets/remove.svg";
import { FeatureClauses, PopularityFilters } from "../../types/CDIFilter";
import { useAuth } from "../../hooks/useAuth";

interface FilterItemPopularityProps {
	name: string;
	defaultMessage: string;
	icon: ReactNode;
	filterKey: keyof PopularityFilters;
	value: FeatureClauses;
	onChange: (filterKey: keyof PopularityFilters, clause: FeatureClauses) => void;
	handleSetOnlyFilter?: MouseEventHandler<HTMLDivElement>;
	allFilters?: PopularityFilters;
	onShowSigninModal: () => void;
}

const FilterItemPopularity = ({
	name,
	defaultMessage,
	icon,
	filterKey,
	value,
	onChange,
	handleSetOnlyFilter,
	allFilters,
	onShowSigninModal,
}: FilterItemPopularityProps) => {
	const [active, setActive] = useState(false);
	const { isAuthenticated } = useAuth();
	
	useEffect(() => {
		if (allFilters) {
			const isActive =
				allFilters[filterKey] === FeatureClauses.Must &&
				Object.entries(allFilters).every(([key, clause]) =>
					key === filterKey || clause !== FeatureClauses.Must,
				);
			setActive(isActive);
		}
	}, [value, filterKey, allFilters]);
	
	const handleCheckboxChange = () => {
		if (!isAuthenticated) {
			onShowSigninModal?.();
			return;
		}
		
		let newClause: FeatureClauses;
		
		if (value === FeatureClauses.None) {
			newClause = FeatureClauses.Must;
		} else if (value === FeatureClauses.Must) {
			newClause = FeatureClauses.MustNot;
		} else {
			newClause = FeatureClauses.None;
		}
		
		onChange(filterKey, newClause);
	};
	
	const handleSetOnlyClick: MouseEventHandler<HTMLDivElement> = (event) => {
		if (!isAuthenticated) {
			onShowSigninModal?.();
			return;
		}
		
		handleSetOnlyFilter?.(event);
	};
	
	const renderCheckboxIcon = () => {
		if (value === FeatureClauses.Must) {
			return <Check style={{ position: "absolute" }} />;
		} else if (value === FeatureClauses.MustNot) {
			return <Remove style={{ position: "absolute", color: "black" }} />;
		}
		return null;
	};
	
	return (
		<div className="filter-item">
			<label className="filter-item-icon-with-label">
				<div className="icon-wrapper">{icon}</div>
				<FormattedMessage id={name} defaultMessage={defaultMessage} />
			</label>
			
			<div
				className={`filter-item-only ${active ? "filter-item-only-active" : ""}`}
				onClick={handleSetOnlyClick}
			>
				<FormattedMessage id="Only" defaultMessage="only" />
			</div>
			
			<div className="filter-item-checkbox" onClick={handleCheckboxChange}>
				<CheckboxBorder />
				{renderCheckboxIcon()}
			</div>
		</div>
	);
};

export default FilterItemPopularity;